import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Clock, Sparkles, Copy, Check, ListChecks, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const CountdownPage = () => {
  const [weddingDate, setWeddingDate] = useState('');
  const [coupleName, setCoupleName] = useState('');
  const [started, setStarted] = useState(false);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [copied, setCopied] = useState(false);

  // Check URL params on load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const date = params.get('date');
    const names = params.get('names');
    if (date) {
      setWeddingDate(date);
      if (names) setCoupleName(decodeURIComponent(names));
      setStarted(true);
    }
  }, []);

  const calculateCountdown = useCallback(() => {
    if (!weddingDate) return;
    const wedding = new Date(weddingDate + 'T00:00:00');
    const now = new Date();
    const diff = wedding.getTime() - now.getTime();

    if (diff <= 0) {
      setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      return;
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    setCountdown({ days, hours, minutes, seconds });
  }, [weddingDate]);

  useEffect(() => {
    if (!started) return;
    calculateCountdown();
    const interval = setInterval(calculateCountdown, 1000);
    return () => clearInterval(interval);
  }, [started, calculateCountdown]);

  const handleStart = (e) => {
    e.preventDefault();
    if (!weddingDate) return;
    setStarted(true);
    const params = new URLSearchParams();
    params.set('date', weddingDate);
    if (coupleName) params.set('names', coupleName);
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  };

  const shareLink = () => {
    const params = new URLSearchParams();
    params.set('date', weddingDate);
    if (coupleName) params.set('names', coupleName);
    const url = `${window.location.origin}/countdown?${params.toString()}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isPast = new Date(weddingDate + 'T00:00:00') <= new Date();
  const wedding = weddingDate ? new Date(weddingDate + 'T00:00:00') : null;

  const milestones = wedding ? [
    { label: 'Book your venue', months: 12 },
    { label: 'Choose your photographer', months: 10 },
    { label: 'Select your dress / suit', months: 8 },
    { label: 'Send save-the-dates', months: 6 },
    { label: 'Finalise guest list', months: 5 },
    { label: 'Book hair & makeup', months: 4 },
    { label: 'Send invitations', months: 3 },
    { label: 'Final dress fitting', months: 1 },
    { label: 'Confirm all vendors', months: 1 },
    { label: 'Rehearsal & final prep', weeks: 1 },
  ].map(m => {
    const d = new Date(wedding);
    if (m.months) d.setMonth(d.getMonth() - m.months);
    if (m.weeks) d.setDate(d.getDate() - m.weeks * 7);
    const isPastMilestone = d <= new Date();
    return { ...m, date: d, isPast: isPastMilestone };
  }) : [];

  if (!started) {
    return (
      <div className="min-h-screen bg-[#FAF9F7]">
        <SEO
          title="Wedding Countdown"
          description="Count down to your big day with our free wedding countdown tool, plus a key planning timeline tailored to your wedding date."
        />
        {/* Hero */}
        <section className="relative">
          <div className="relative h-[300px] md:h-[400px] overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1519741497674-611481863552?w=1600&h=800&fit=crop"
              alt="Wedding countdown"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-[#FAF9F7]"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center px-4">
                <p className="text-white/90 text-xs tracking-[0.3em] uppercase mb-4">PLAN YOUR DAY</p>
                <h1
                  className="text-4xl md:text-6xl text-white mb-4 tracking-tight drop-shadow-lg"
                  style={{ fontFamily: '"Playfair Display", serif' }}
                >
                  Wedding Countdown
                </h1>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="max-w-lg mx-auto px-4">
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-[#E5E0DA]">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-[#FDF2F4] rounded-full flex items-center justify-center mx-auto mb-5">
                  <Heart size={28} style={{ color: '#DE92A9' }} fill="#DE92A9" />
                </div>
                <h2
                  className="text-2xl md:text-3xl text-[#2C2C2C] mb-3"
                  style={{ fontFamily: '"Playfair Display", serif' }}
                >
                  When's the big day?
                </h2>
                <p className="text-[#757B7F] text-sm">
                  Enter your wedding date to start counting down to the most special day of your life.
                </p>
              </div>

              <form onSubmit={handleStart} className="space-y-5">
                <div>
                  <label className="block text-xs tracking-wider uppercase text-[#757B7F] mb-2">Your Names (optional)</label>
                  <input
                    type="text"
                    value={coupleName}
                    onChange={(e) => setCoupleName(e.target.value)}
                    placeholder="e.g. Sarah & James"
                    className="w-full px-4 py-3 border border-[#E5E0DA] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#DE92A9]/20 focus:border-[#DE92A9] text-[#2C2C2C] placeholder-[#C0B9B0]"
                    data-testid="couple-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs tracking-wider uppercase text-[#757B7F] mb-2">Wedding Date</label>
                  <input
                    type="date"
                    value={weddingDate}
                    onChange={(e) => setWeddingDate(e.target.value)}
                    required
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border border-[#E5E0DA] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#DE92A9]/20 focus:border-[#DE92A9] text-[#2C2C2C]"
                    data-testid="wedding-date-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#DE92A9] hover:bg-[#C9758F] text-white py-6 text-sm tracking-wider rounded-lg"
                  data-testid="start-countdown-btn"
                >
                  <Sparkles size={18} className="mr-2" />
                  START MY COUNTDOWN
                </Button>
              </form>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      {/* Countdown Hero */}
      <section className="relative overflow-hidden" style={{ backgroundColor: '#DE92A9' }}>
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1519741497674-611481863552?w=1920&h=800&fit=crop"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>

        <div className="relative z-10 py-16 md:py-24 px-4">
          <div className="max-w-4xl mx-auto text-center">
            {coupleName && (
              <p className="text-white/80 text-sm tracking-[0.3em] uppercase mb-3 animate-fade-in">
                {coupleName}
              </p>
            )}
            <h1
              className="text-3xl md:text-5xl text-white mb-2"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              {isPast ? 'Congratulations!' : 'Counting Down To'}
            </h1>
            {!isPast && (
              <p className="text-white/70 text-lg mb-10">
                {wedding?.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            )}

            {isPast ? (
              <div className="py-8">
                <Heart size={64} className="mx-auto text-white mb-4" fill="white" />
                <p
                  className="text-3xl text-white"
                  style={{ fontFamily: '"Playfair Display", serif' }}
                >
                  You're married!
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-3 md:gap-6 max-w-2xl mx-auto mb-10">
                {[
                  { value: countdown.days, label: 'Days' },
                  { value: countdown.hours, label: 'Hours' },
                  { value: countdown.minutes, label: 'Minutes' },
                  { value: countdown.seconds, label: 'Seconds' },
                ].map((item) => (
                  <div key={item.label} className="text-center">
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 md:p-6 border border-white/20">
                      <span
                        className="text-4xl md:text-6xl text-white font-light tabular-nums"
                        style={{ fontFamily: '"Playfair Display", serif' }}
                      >
                        {String(item.value).padStart(2, '0')}
                      </span>
                    </div>
                    <p className="text-white/60 text-xs tracking-widest uppercase mt-3">{item.label}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Share Button */}
            <button
              onClick={shareLink}
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-full text-sm tracking-wider transition-all border border-white/20"
              data-testid="share-countdown-btn"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? 'LINK COPIED!' : 'SHARE YOUR COUNTDOWN'}
            </button>
          </div>
        </div>
      </section>

      {/* Planning Timeline */}
      {!isPast && milestones.length > 0 && (
        <section className="py-16">
          <div className="max-w-3xl mx-auto px-4">
            <div className="text-center mb-12">
              <p className="text-xs tracking-[0.3em] uppercase mb-3" style={{ color: '#DE92A9' }}>
                YOUR PLANNING TIMELINE
              </p>
              <h2
                className="text-3xl md:text-4xl text-[#2C2C2C] mb-4"
                style={{ fontFamily: '"Playfair Display", serif' }}
              >
                Key Milestones
              </h2>
              <div className="w-20 h-0.5 mx-auto" style={{ backgroundColor: '#DE92A9' }}></div>
            </div>

            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-6 top-0 bottom-0 w-px bg-[#E5E0DA]"></div>

              <div className="space-y-6">
                {milestones.map((milestone) => (
                  <div key={milestone.label} className="relative flex items-start gap-6 pl-2">
                    {/* Dot */}
                    <div
                      className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-all ${
                        milestone.isPast
                          ? 'bg-[#DE92A9] border-[#DE92A9]'
                          : 'bg-white border-[#E5E0DA]'
                      }`}
                    >
                      {milestone.isPast ? (
                        <Check size={16} className="text-white" />
                      ) : (
                        <Clock size={14} className="text-[#C0B9B0]" />
                      )}
                    </div>

                    {/* Content */}
                    <div className={`flex-1 pb-2 ${milestone.isPast ? 'opacity-50' : ''}`}>
                      <div className="bg-white rounded-lg p-5 border border-[#E5E0DA] shadow-sm">
                        <div className="flex items-center justify-between">
                          <h3 className={`font-medium text-sm ${milestone.isPast ? 'text-[#757B7F] line-through' : 'text-[#2C2C2C]'}`}>
                            {milestone.label}
                          </h3>
                          <span className="text-xs text-[#C0B9B0]">
                            {milestone.date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Reset */}
      <section className="py-10 text-center space-y-6">
        <Link
          to="/checklist"
          className="inline-flex items-center gap-2 text-xs tracking-[0.2em] font-semibold px-8 py-4 text-white rounded-full transition-all hover:gap-3 group"
          style={{ backgroundColor: '#DE92A9' }}
          data-testid="countdown-checklist-link"
        >
          <ListChecks size={14} />
          OPEN THE WEDDING CHECKLIST
          <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
        </Link>
        <div>
        <button
          onClick={() => {
            setStarted(false);
            setWeddingDate('');
            setCoupleName('');
            window.history.replaceState({}, '', window.location.pathname);
          }}
          className="text-[#DE92A9] text-sm underline hover:no-underline"
          data-testid="reset-countdown-btn"
        >
          Start a new countdown
        </button>
        </div>
      </section>
    </div>
  );
};

export default CountdownPage;
